import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './markdown-html-converter.component.module.scss';

interface MdState {
    input: string;
    output: string;
    direction: 'md-to-html' | 'html-to-md';
    outputTab: 'preview' | 'source';
    copiedField: string | null;
    warning: string;
}

const SAMPLE_MD = `# Hello World

This is **bold** and *italic* text.

- Item 1
- Item 2
- Item 3

> A blockquote example.`;

export default class MarkdownHtmlConverterComponent extends Component<{ feature?: string }, MdState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private marked: any = null;
    private turndown: any = null;

    constructor(props: { feature?: string }) {
        super(props);
        const isHtmlToMd = props.feature === 'convert-html-to-markdown';
        this.state = {
            input: isHtmlToMd ? '<h1>Hello</h1>\n<p>This is <strong>bold</strong> and <em>italic</em>.</p>' : SAMPLE_MD,
            output: '', direction: isHtmlToMd ? 'html-to-md' : 'md-to-html',
            outputTab: isHtmlToMd ? 'source' : 'preview', copiedField: null, warning: '',
        };
    }

    componentDidMount() { this.convert(this.state.input); }

    async loadMarked() {
        if (!this.marked) { this.marked = (await import('marked')).marked; }
        return this.marked;
    }

    async loadTurndown() {
        if (!this.turndown) {
            const TurndownService = (await import('turndown')).default;
            this.turndown = new TurndownService({ headingStyle: 'atx', codeBlockStyle: 'fenced' });
        }
        return this.turndown;
    }

    async convert(input: string) {
        if (!input.trim()) { this.setState({ output: '', warning: '' }); return; }

        if (this.state.direction === 'md-to-html') {
            const marked = await this.loadMarked();
            const output = marked(input);
            this.setState({ output, warning: '' });
        } else {
            const td = await this.loadTurndown();
            try {
                const output = td.turndown(input);
                this.setState({ output, warning: '' });
            } catch {
                this.setState({ warning: 'Some HTML elements could not be converted to Markdown' });
            }
        }
    }

    handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const input = e.target.value;
        this.setState({ input });
        this.convert(input);
    };

    toggleDirection = () => {
        this.setState(s => {
            const direction = s.direction === 'md-to-html' ? 'html-to-md' : 'md-to-html';
            const input = s.output;
            return { direction, input, output: '', outputTab: direction === 'md-to-html' ? 'preview' : 'source', warning: '' } as any;
        }, () => this.convert(this.state.input));
    };

    copyOutput = () => {
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'output' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    clear = () => this.setState({ input: '', output: '', warning: '', copiedField: null });

    render() {
        const { input, output, direction, outputTab, copiedField, warning } = this.state;
        const isMdToHtml = direction === 'md-to-html';
        const isCopied = copiedField === 'output';

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.toolbar}>
                            <div className={styles.directionToggle}>
                                <button className={`${styles.dirBtn} ${isMdToHtml ? styles.dirActive : ''}`}
                                    onClick={() => isMdToHtml || this.toggleDirection()}>MD → HTML</button>
                                <button className={`${styles.dirBtn} ${!isMdToHtml ? styles.dirActive : ''}`}
                                    onClick={() => !isMdToHtml || this.toggleDirection()}>HTML → MD</button>
                            </div>
                            <button className={sharedStyles.copyButton} onClick={this.clear}>✕ Clear</button>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>{isMdToHtml ? 'Markdown' : 'HTML'}</label>
                        <textarea className={sharedStyles.textarea} value={input} onChange={this.handleInput} rows={14}
                            placeholder={isMdToHtml ? 'Type Markdown here...' : 'Paste HTML here...'} />
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className={genStyles.section}>
                        <div className={styles.outputHeader}>
                            {isMdToHtml && (
                                <div className={styles.outputTabs}>
                                    <button className={`${styles.outTab} ${outputTab === 'preview' ? styles.outTabActive : ''}`}
                                        onClick={() => this.setState({ outputTab: 'preview' })}>Preview</button>
                                    <button className={`${styles.outTab} ${outputTab === 'source' ? styles.outTabActive : ''}`}
                                        onClick={() => this.setState({ outputTab: 'source' })}>HTML</button>
                                </div>
                            )}
                            {!isMdToHtml && <label className={sharedStyles.label}>Markdown</label>}
                        </div>

                        {isMdToHtml && outputTab === 'preview' ? (
                            <div className={`${styles.preview} ${sharedStyles.textarea}`}
                                dangerouslySetInnerHTML={{ __html: output }} />
                        ) : (
                            <div className={styles.readOnlyWrapper}>
                                <span className={styles.readOnlyBadge}>Read Only</span>
                                <textarea className={sharedStyles.textarea} value={output} readOnly rows={14} style={{ background: '#f1f5f9' }} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className={genStyles.section}>
                        {warning && <div className={sharedStyles.warningMessage}>⚠ {warning}</div>}
                        <div className={styles.footer}>
                            <button className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyOutput}>
                                {isCopied ? '✓ Copied' : isMdToHtml ? 'Copy HTML' : 'Copy Markdown'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
